import type { Faq as FaqType } from 'libs/http/api/content/content.types';

import type { AccessRoute } from 'ui/organisms/AccessRoutes/AccessRoutes.types';

type Translate = (key: string) => string;

export const getAccessRoutes = (t: Translate): AccessRoute[] => [
  {
    name: t('Platform'),
    desc: t('Search, screen and export company records from the web application.'),
    actions: [{ label: t('Open the platform'), href: '/platform' }],
  },
  {
    name: 'API',
    desc: t('Query any company by registry ID — documented endpoints, JSON responses, provenance on every value.'),
    actions: [{ label: t('Read the API docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Bulk data feeds'),
    desc: t('Full datasets and scheduled deltas, delivered to your warehouse on your cadence.'),
    actions: [{ label: t('Request a feed'), href: '/book-a-demo' }],
  },
  {
    name: 'CRM',
    desc: t(
      'HubSpot, Salesforce and Microsoft Dynamics. Registry fields on the record your team already works in, matched on registration number.',
    ),
    actions: [
      { label: t('Install for HubSpot'), href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
      { label: t('Install for Salesforce'), href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
    ],
  },
  {
    name: 'MCP',
    desc: t('Connect the dataset to any MCP-compatible agent or tool, on the same key.'),
    actions: [{ label: t('MCP server'), href: '/mcp-integration' }],
  },
  {
    name: t('Assistant connectors'),
    desc: t('Pull registry data straight into your AI assistant, with the source attached to the answer.'),
    actions: [
      { label: 'Add to Claude', href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: 'Add to ChatGPT', href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
  {
    name: 'Regis AI',
    desc: t(
      'Ask questions in natural language across the dataset and get sourced answers — in the platform, or over the API.',
    ),
    actions: [
      { label: 'Regis AI', href: '/regis' },
      { label: 'Regis API', href: '/regis-api' },
    ],
  },
];

export const getFaqItems = (t: Translate): FaqType[] => [
  {
    id: 'data-source',
    question: t('Where does the data actually come from?'),
    answer: t(
      'Primary government sources — company registers, tax authorities, filing bodies and official gazettes — read directly by our own pipelines across 400+ sources in 200+ countries. Every value carries the register it came from, the filing reference and the moment it was read — attached at field level, not as a footnote on the response. Where a register does not publish a field, the API returns null and names the register that is silent, rather than filling the gap.',
    ),
    default_opened: true,
  },
  {
    id: 'freshness',
    question: t('How fresh is it?'),
    answer: t(
      'Sources are synced daily and detected changes are pushed to your webhook endpoint. Freshness follows the underlying register: some jurisdictions publish continuously, others in batches. The payload always states when the record was filed and when we read it.',
    ),
  },
  {
    id: 'redistribution',
    question: t('Can we redistribute the data inside our own product?'),
    answer: t(
      'Yes, under a redistribution licence. Settle this before you build. It is where technology teams get caught by agreements written for internal analyst use — ask for the specific clause in writing.',
    ),
  },
  {
    id: 'proprietary-identifier',
    question: t('Do we have to adopt a proprietary company identifier?'),
    answer: t(
      'No. Records key on open identifiers — registration number, jurisdiction and VAT number. You can join our records to what you already hold without adopting a key that only works while you remain a customer.',
    ),
  },
  {
    id: 'match-quality',
    question: t('How do we evaluate match quality before committing?'),
    answer: t(
      'Batch-resolve a sample of your own records with a test key and read the match confidence distribution. Evaluating on your own messy inputs tells you more than any coverage statistic, including ours.',
    ),
  },
  {
    id: 'agent-access',
    question: t('Can an agent call this directly?'),
    answer: t(
      'Yes. The MCP server exposes search, details, ownership, financials, group structure and screening as tools, under the same authentication as the REST API. Regis answers from what it retrieved for that query rather than from training, so an agent that finds nothing reports nothing.',
    ),
  },
  {
    id: 'gdpr',
    question: t('What about personal data and GDPR?'),
    answer: t(
      'Officer and beneficial-owner information is published by the registers themselves under statutory transparency regimes, and we carry the source and legal basis with the record. Data protection documentation, including processing terms and the sub-processor list, is available on request during procurement.',
    ),
  },
  {
    id: 'model-training',
    question: t('Can we use it to train or ground a model?'),
    answer: t(
      'Grounding and retrieval are covered under standard terms, and the source block gives model output something citable. Training licences are separate and priced separately.',
    ),
  },
];
